import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';

const ClientRevenueTable = ({ data = [], loading = false }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'totalRevenue', direction: 'desc' });

  const formatCurrency = (value) => {
    return `$${value?.toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` ;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date?.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getClientTypeLabel = (type) => {
    const labels = {
      'individual': 'Individual',
      'corporate': 'Corporativo',
      'fleet': 'Flotilla'
    };
    return labels?.[type] || type;
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig?.key === key && sortConfig?.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedData = React.useMemo(() => {
    if (!data || data?.length === 0) return [];

    const sorted = [...data]?.sort((a, b) => {
      const aValue = a?.[sortConfig?.key];
      const bValue = b?.[sortConfig?.key];

      if (typeof aValue === 'string') {
        return sortConfig?.direction === 'asc'
          ? aValue?.localeCompare(bValue)
          : bValue?.localeCompare(aValue);
      }

      return sortConfig?.direction === 'asc'
        ? aValue - bValue
        : bValue - aValue;
    });

    return sorted;
  }, [data, sortConfig]);

  const SortIcon = ({ columnKey }) => {
    if (sortConfig?.key !== columnKey) {
      return <Icon name="ChevronsUpDown" size={16} className="text-muted-foreground" />;
    }
    return sortConfig?.direction === 'asc'
      ? <Icon name="ChevronUp" size={16} className="text-primary" />
      : <Icon name="ChevronDown" size={16} className="text-primary" />;
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-elevation-1 p-4 md:p-6">
      <div className="flex items-center gap-2 mb-4 md:mb-6">
        <Icon name="Users" size={24} className="text-primary" />
        <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground">
          Ingresos por Cliente
        </h2>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-32">
          <Icon name="Loader2" className="w-8 h-8 text-blue-600 animate-spin" />
        </div>
      ) : sortedData?.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-32">
          <Icon name="UserX" size={48} className="text-muted-foreground mb-2" />
          <p className="text-sm caption text-muted-foreground">
            No hay datos de clientes disponibles
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4">
                  <button
                    onClick={() => handleSort('clientName')}
                    className="flex items-center gap-1 text-sm font-semibold text-foreground hover:text-primary transition-smooth"
                  >
                    Cliente
                    <SortIcon columnKey="clientName" />
                  </button>
                </th>
                <th className="text-left py-3 px-4">
                  <button
                    onClick={() => handleSort('clientType')}
                    className="flex items-center gap-1 text-sm font-semibold text-foreground hover:text-primary transition-smooth"
                  >
                    Tipo
                    <SortIcon columnKey="clientType" />
                  </button>
                </th>
                <th className="text-right py-3 px-4">
                  <button
                    onClick={() => handleSort('totalRevenue')}
                    className="flex items-center justify-end gap-1 text-sm font-semibold text-foreground hover:text-primary transition-smooth w-full"
                  >
                    Ingresos Totales
                    <SortIcon columnKey="totalRevenue" />
                  </button>
                </th>
                <th className="text-right py-3 px-4">
                  <button
                    onClick={() => handleSort('serviceCount')}
                    className="flex items-center justify-end gap-1 text-sm font-semibold text-foreground hover:text-primary transition-smooth w-full"
                  >
                    Servicios
                    <SortIcon columnKey="serviceCount" />
                  </button>
                </th>
                <th className="text-right py-3 px-4">
                  <button
                    onClick={() => handleSort('averageInvoiceValue')}
                    className="flex items-center justify-end gap-1 text-sm font-semibold text-foreground hover:text-primary transition-smooth w-full"
                  >
                    Valor Promedio
                    <SortIcon columnKey="averageInvoiceValue" />
                  </button>
                </th>
                <th className="text-left py-3 px-4">
                  <button
                    onClick={() => handleSort('lastServiceDate')}
                    className="flex items-center gap-1 text-sm font-semibold text-foreground hover:text-primary transition-smooth"
                  >
                    Último Servicio
                    <SortIcon columnKey="lastServiceDate" />
                  </button>
                </th>
                <th className="text-right py-3 px-4">
                  <button
                    onClick={() => handleSort('clientLifetimeValue')}
                    className="flex items-center justify-end gap-1 text-sm font-semibold text-foreground hover:text-primary transition-smooth w-full"
                  >
                    CLV
                    <SortIcon columnKey="clientLifetimeValue" />
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedData?.map((client) => (
                <tr
                  key={client?.clientId}
                  className="border-b border-border hover:bg-muted/50 transition-smooth"
                >
                  <td className="py-3 px-4">
                    <span className="text-sm font-medium text-foreground">
                      {client?.clientName}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary">
                      {getClientTypeLabel(client?.clientType)}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <span className="text-sm font-semibold text-success">
                      {formatCurrency(client?.totalRevenue)}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <span className="text-sm text-foreground">
                      {client?.serviceCount}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <span className="text-sm text-foreground">
                      {formatCurrency(client?.averageInvoiceValue)}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-sm text-muted-foreground">
                      {formatDate(client?.lastServiceDate)}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <span className="text-sm font-semibold text-warning">
                      {formatCurrency(client?.clientLifetimeValue)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ClientRevenueTable;